// Modelos del módulo "Correos enviados".
//
// IMPORTANTE: el contrato de la API de correos NO usa el shape de página de
// Spring Data ({ number, first, last, empty }) que usa el resto del CRM.
// Usa este PageResponse propio: { content, page, size, totalElements,
// totalPages, hasNext, hasPrevious }. Se declara un tipo local específico
// para no confundirlo con el PageResponse compartido.

/** Página devuelta por los endpoints de /api/emails/** (NO es el shape de Spring Data). */
export interface EmailPageResponse<T> {
  content: T[];
  page: number;          // 0-indexed (página actual)
  size: number;          // tamaño de página efectivo
  totalElements: number; // total de registros
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/** Estados posibles de un correo (columna status, case-sensitive). */
export type EmailStatus =
  | 'QUEUED'
  | 'SENDING'
  | 'SENT'
  | 'DELIVERED'
  | 'FAILED'
  | 'BOUNCED'
  | 'DEFERRED';

/** Ítem de listado — GET /api/emails/mine y /api/emails/by-contact/{id}. */
export interface EmailMessageSummaryResponse {
  id: number;
  senderType: string;            // "USER" | "SYSTEM"
  purpose: string;               // EmailPurpose como texto, ej. "CONTACT_MESSAGE"
  channelType: string | null;    // EmailChannelType como texto, ej. "USER_GMAIL"
  channelRefId: number | null;
  status: EmailStatus | string;
  fromEmail: string;
  fromName: string | null;
  subject: string | null;
  openCount: number;
  clickCount: number;
  sentAt: string | null;         // OffsetDateTime ISO-8601
  deliveredAt: string | null;
  firstOpenedAt: string | null;
  createdAt: string;
  idContact: number | null;
  contextEntityType: string | null;
  contextEntityId: number | null;
  // Agregados por el backend: nombres legibles para mostrar en la lista sin abrir el detalle.
  senderName: string | null;     // user.fullname -> fromName -> fromEmail (fallback resuelto en backend)
  recipientName: string | null;  // recipient.fullName -> contact.fullname
  recipientEmail: string | null; // recipient.email -> contact.mail
}

/** Destinatario de un correo (recipients[] del detalle). */
export interface EmailRecipientResponse {
  id: number;
  kind: string;                  // "TO" | "CC" | "BCC" | ...
  email: string;
  fullName: string | null;
  idContact: number | null;
  idUser: number | null;
}

/** Adjunto de un correo (attachments[] del detalle). */
export interface EmailAttachmentResponse {
  id: number;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storageRef: string | null;
}

/** Evento de la línea de tiempo de un correo (events[] del detalle). */
export interface EmailEventResponse {
  id: number;
  eventType: string;             // OPENED | CLICKED | DELIVERED | ...
  occurredAt: string;            // OffsetDateTime ISO-8601
  providerRaw: string | null;
  ipAddress: string | null;
  userAgent: string | null;
}

/** Detalle completo — GET /api/emails/{id}. */
export interface EmailMessageDetailResponse {
  id: number;
  senderType: string;
  purpose: string;
  channelType: string | null;
  channelRefId: number | null;
  status: EmailStatus | string;
  fromEmail: string;
  fromName: string | null;
  replyTo: string | null;
  subject: string | null;
  preheader: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  templateCode: string | null;
  providerMessageId: string | null;
  errorMessage: string | null;
  openCount: number;
  clickCount: number;
  attemptCount: number;
  scheduledAt: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  nextAttemptAt: string | null;
  createdAt: string;
  updatedAt: string;
  idContact: number | null;
  contextEntityType: string | null;
  contextEntityId: number | null;
  recipients: EmailRecipientResponse[];
  attachments: EmailAttachmentResponse[];
  events: EmailEventResponse[];
}

/** Filtros para GET /api/emails/mine. businessId NO se envía (sale del JWT). */
export interface MyEmailsSearchParams {
  status?: string;
  purpose?: string;
  idContact?: number;
  from?: string;   // ISO-8601 con offset
  to?: string;     // ISO-8601 con offset
  page?: number;
  size?: number;
}

/** Filtros para GET /api/emails/by-contact/{contactId}. businessId NO se envía. */
export interface ContactEmailsSearchParams {
  status?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

// ── Timeline unificado (enviados + recibidos) ────────────────────────────
// GET /api/emails/timeline/by-contact/{contactId}

/** Dirección de un ítem del timeline. Nunca null. */
export type TimelineDirection = 'SENT' | 'RECEIVED';

/** Origen de un ítem del timeline. Nunca null. */
export type TimelineSourceType = 'EMAIL_MESSAGE' | 'INBOX_MESSAGE';

/**
 * Ítem del timeline unificado de correos con un contacto.
 * Nullability por dirección:
 *  - SENT:     status=valor, isRead=null; counterpart* desde el destinatario.
 *  - RECEIVED: status=null,  isRead=valor; counterpart* desde el remitente.
 */
export interface TimelineItemResponse {
  direction: TimelineDirection;
  occurredAt: string | null;       // OffsetDateTime UTC (puede ser null en RECEIVED)
  subject: string | null;
  channelType: string | null;      // SYSTEM_API | USER_GMAIL | USER_MICROSOFT | USER_SMTP
  counterpartName: string | null;  // persona del otro lado
  counterpartEmail: string | null;
  snippet: string | null;          // preview corto
  status: EmailStatus | string | null; // SOLO SENT; null en RECEIVED
  isRead: boolean | null;          // SOLO RECEIVED; null en SENT
  sourceType: TimelineSourceType;
  sourceId: number;                // id de la fila origen (email_message.id o email_inbox_message.id)
  /** Nombre del enum EmailPurpose (MARKETING_CAMPAIGN, CONTACT_MESSAGE, ...). SOLO SENT; null en RECEIVED. */
  purpose?: string | null;
  /** true = envío masivo (campaña/invitación masiva); false = personal. SOLO SENT; null en RECEIVED. */
  bulk?: boolean | null;
}

/** Filtros para el timeline por contacto. Solo paginación (no acepta status/fecha/businessId). */
export interface TimelineSearchParams {
  page?: number;
  size?: number;
}

/**
 * Filtros para el timeline GLOBAL del usuario (GET /api/emails/timeline/mine).
 * businessId NO se envía (sale del JWT).
 */
export interface MyTimelineSearchParams {
  /** "SENT" | "RECEIVED" (case-insensitive). Omitir = ambas direcciones. */
  direction?: 'SENT' | 'RECEIVED';
  /** Texto libre ILIKE %q% sobre asunto + contraparte + snippet (ambas direcciones). */
  q?: string;
  /** true = solo recibidos no leídos (excluye enviados). Default false. */
  unreadOnly?: boolean;
  /** Filtra enviados+recibidos a este contacto (conversación con el contacto). */
  contactId?: number;
  /** Filtra por correo de la contraparte (case-insensitive); contacto guardado o no. */
  email?: string;
  page?: number;
  size?: number;
}

// ── Detalle de correo RECIBIDO ───────────────────────────────────────────
// GET /api/emails/inbox-message/{id}

/** Dirección de una entrada de `addresses` en un correo recibido. */
export type InboxAddressKind = 'FROM' | 'TO' | 'CC' | 'BCC';

/** Una dirección asociada a un correo recibido (addresses[]). */
export interface InboxAddressResponse {
  kind: InboxAddressKind | string;
  email: string | null;
  idContact: number | null;
}

/**
 * Detalle de un correo RECIBIDO persistido (email_inbox_message).
 * GET /api/emails/inbox-message/{id} — id == sourceId del ítem RECEIVED.
 */
export interface InboxMessageDetailResponse {
  id: number;
  subject: string | null;
  fromEmail: string | null;
  fromName: string | null;
  receivedAt: string | null;     // OffsetDateTime UTC
  channelType: string | null;    // USER_GMAIL | USER_MICROSOFT | USER_SMTP
  isRead: boolean | null;
  hasAttachments: boolean | null;
  bodyHtml: string | null;       // null si el cuerpo aún no se trajo
  bodyText: string | null;       // null si el cuerpo aún no se trajo
  idContact: number | null;
  threadId: string | null;
  addresses: InboxAddressResponse[]; // nunca null (puede venir vacío)
}

/** Motivos posibles de un sync no exitoso. null si synced=true. */
export type ContactInboxSyncReason =
  | 'COOLDOWN'
  | 'NO_IMAP_CONNECTION'
  | 'NO_CONTACT_EMAIL'
  | 'CONTACT_NOT_FOUND'
  | 'SYNC_ERROR';

/**
 * Respuesta de POST /api/emails/timeline/by-contact/{contactId}/sync.
 * Siempre 200 salvo error de auth/permiso.
 * Regla de recarga: recargar el GET timeline SOLO si synced && newMessages > 0.
 */
export interface ContactInboxSyncResponse {
  synced: boolean;     // true si se leyó el buzón IMAP del usuario
  skipped: boolean;    // true SOLO en cooldown (no se tocó IMAP)
  newMessages: number; // recibidos NUEVOS insertados en esta corrida
  reason: ContactInboxSyncReason | null;
}

// ── Envío de correo suelto (compose) ─────────────────────────────────────
// POST /api/emails/send

/** Tipo de destinatario en el envío. */
export type SendRecipientKind = 'TO' | 'CC' | 'BCC';

/**
 * Un destinatario del envío.
 * - Correo escrito a mano: solo `email` (+ `fullName` opcional), sin `idContact`.
 * - Contacto seleccionado del CRM: `email` (del contacto) + `idContact`.
 */
export interface SendEmailRecipient {
  kind: SendRecipientKind;
  email: string;
  fullName?: string;
  idContact?: number;
}

/** Adjunto del envío (requiere subida previa que genere `storageRef`). */
export interface SendEmailAttachment {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storageRef: string;
}

/**
 * Body de POST /api/emails/send. El remitente (idUserSender) y businessId
 * se derivan del JWT — NO se envían en el body.
 * Para correo libre: purpose='CONTACT_MESSAGE', subject + body, sin templateCode.
 */
export interface SendEmailDto {
  purpose: string;                    // libre → siempre 'CONTACT_MESSAGE'
  recipients: SendEmailRecipient[];   // >= 1
  subject: string;                    // max 500
  bodyHtml?: string;
  bodyText?: string;
  idContact?: number;                 // contacto principal (link al timeline)
  // Contexto de origen (Inc 24): al enviar desde un lead/deal/etc. el backend
  // registra el correo en el timeline de esa entidad. Ej. LEAD + leadId.
  contextEntityType?: string;         // "LEAD" | "DEAL" | "COMPANY" | "CONTACT"
  contextEntityId?: number;
  attachments?: SendEmailAttachment[];
}

/** Respuesta 202 ACCEPTED de POST /api/emails/send. El correo queda encolado. */
export interface SendEmailResponse {
  id: number;
  status: string;     // típicamente 'QUEUED'
}

/** Estados disponibles para selects de filtro (UI). */
export const EMAIL_STATUS_OPTIONS: { value: EmailStatus; label: string }[] = [
  { value: 'QUEUED', label: 'En cola' },
  { value: 'SENDING', label: 'Enviando' },
  { value: 'SENT', label: 'Enviado' },
  { value: 'DELIVERED', label: 'Entregado' },
  { value: 'FAILED', label: 'Fallido' },
  { value: 'BOUNCED', label: 'Rebotado' },
  { value: 'DEFERRED', label: 'Diferido' },
];

/**
 * Canales de correo, en español. Diccionario único del módulo Correos:
 * ningún panel debe pintar `USER_GMAIL` u otra llave técnica.
 */
export const EMAIL_CHANNEL_TYPE_LABELS: Readonly<Record<string, string>> = {
  SYSTEM_API: 'Correo del sistema',
  USER_GMAIL: 'Gmail del usuario',
  USER_MICROSOFT: 'Outlook del usuario',
  USER_SMTP: 'Servidor SMTP propio',
  BUSINESS_SMTP: 'Servidor SMTP de la empresa',
};

export function emailChannelTypeLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return EMAIL_CHANNEL_TYPE_LABELS[value] ?? value;
}
